import { apiGet, apiPost, apiPut } from '../client';
import {
  FiscalProfileResponseSchema,
  InvoiceResponseSchema,
  InvoiceSendResponseSchema,
  InvoicesResponseSchema,
  InvoiceSettingsResponseSchema,
  PublicInvoiceResponseSchema,
  type FiscalProfile,
  type InvoiceRecord,
  type InvoiceSettings,
  type InvoiceSend,
  type InvoicesResponse,
  type PublicInvoice,
} from '../schemas';

export type SaveFiscalProfileInput = {
  ruc: string;
  dv: string;
  razonSocial: string;
  direccion: string;
  itbmsDefaultRateBp?: number;
  /** The version the form read, 0 when there was no profile yet; a stale one comes back 409 version_conflict. */
  expectedVersion?: number;
};

export type CreateInvoiceInput = {
  clientName?: string;
  clientRuc?: string;
  clientDv?: string;
  itbmsRateBp?: number;
};

/** 404 while the business has not configured its datos fiscales yet. */
export async function getFiscalProfile(): Promise<FiscalProfile> {
  return (await apiGet('/v1/business/fiscal-profile', FiscalProfileResponseSchema)).data;
}

export async function saveFiscalProfile(input: SaveFiscalProfileInput): Promise<FiscalProfile> {
  return (await apiPut('/v1/business/fiscal-profile', FiscalProfileResponseSchema, input)).data;
}

/** "Emitir factura al cobrar": readable with facturas or negocio. */
export async function getInvoiceSettings(): Promise<InvoiceSettings> {
  return (await apiGet('/v1/business/invoice-settings', InvoiceSettingsResponseSchema)).data;
}

/** Written with negocio only; a fresh key per toggle, so a retry replays and a new toggle applies. */
export async function saveInvoiceSettings(
  input: InvoiceSettings,
  idempotencyKey: string,
): Promise<InvoiceSettings> {
  return (
    await apiPut('/v1/business/invoice-settings', InvoiceSettingsResponseSchema, input, idempotencyKey)
  ).data;
}

export async function createInvoiceForCharge(
  chargeId: string,
  input: CreateInvoiceInput,
  idempotencyKey: string,
): Promise<InvoiceRecord> {
  return (
    await apiPost(
      `/v1/business/charges/${chargeId}/invoice`,
      InvoiceResponseSchema,
      input,
      idempotencyKey,
    )
  ).data;
}

export function listInvoices(): Promise<InvoicesResponse> {
  return apiGet('/v1/business/invoices', InvoicesResponseSchema);
}

export async function getInvoice(invoiceId: string): Promise<InvoiceRecord> {
  return (await apiGet(`/v1/business/invoices/${invoiceId}`, InvoiceResponseSchema)).data;
}

export async function voidInvoice(
  invoiceId: string,
  reason: string,
  idempotencyKey: string,
): Promise<InvoiceRecord> {
  return (
    await apiPost(
      `/v1/business/invoices/${invoiceId}/void`,
      InvoiceResponseSchema,
      { reason },
      idempotencyKey,
    )
  ).data;
}

/**
 * Manda la factura al correo del cliente. The key is minted per press of the
 * button, so a replay after a dropped response answers with the send that
 * already happened and a deliberate retry actually retries.
 */
export async function sendInvoiceEmail(
  invoiceId: string,
  email: string,
  idempotencyKey: string,
): Promise<InvoiceSend> {
  return (
    await apiPost(
      `/v1/business/invoices/${invoiceId}/send`,
      InvoiceSendResponseSchema,
      { email },
      idempotencyKey,
    )
  ).data;
}

/**
 * La factura pública, sin sesión. Reachable by anybody holding the link, which
 * is the whole point: the customer opens it and prints it themselves.
 */
export async function getPublicInvoice(shareToken: string): Promise<PublicInvoice> {
  return (await apiGet(`/v1/factura/${shareToken}`, PublicInvoiceResponseSchema)).data;
}

/** The link handed to a customer, on whatever origin this panel is served from. */
export function publicInvoiceUrl(origin: string, shareToken: string): string {
  return `${origin.replace(/\/$/, '')}/factura/${shareToken}`;
}
